import { useState } from "react";
import { Alert, Empty, Select, Spin } from "antd";

import {
  OVERLAY_ECG_MAX_POINTS,
  useEcgWaveformQuery,
} from "@/hooks/useWaveformQueries";
import type { ExtraTrace } from "./EcgLeadStrip";
import { EcgLeadStrip } from "./EcgLeadStrip";
import { ECG_LEAD_NAMES, PX_PER_MM } from "./constants";

const GAIN_OPTIONS_MM_PER_MV = [5, 10, 20];
const SPEED_OPTIONS_MM_PER_S = [12.5, 25, 50];
const DEFAULT_GAIN_MM_PER_MV = 10;
const DEFAULT_SPEED_MM_PER_S = 25;
const DEFAULT_LEAD = "II";

export interface CompareStudy {
  imagingStudyId: string;
  label: string;
  color: string;
}

// ---------------------------------------------------------------------------
// Internal hook helpers
// ---------------------------------------------------------------------------

interface StudyLeadResult {
  timesMs: number[] | undefined;
  amplitudesUv: number[] | undefined;
  isLoading: boolean;
  error: unknown;
}

/** Fetches one study's waveform and extracts the requested lead.
 *
 * Only the displayed lead is requested (1 of 12) at the overlay point budget:
 * an overlay fetches one of these per compared study, and a full-detail
 * 12-lead payload for each is ~40× the bytes (282 KB vs 6.7 KB for a 10 s
 * record) to draw the same single trace.
 */
function useStudyLead(
  imagingStudyId: string,
  leadName: string,
): StudyLeadResult {
  const { data, isLoading, error } = useEcgWaveformQuery({
    imagingStudyId,
    leads: [leadName],
    maxPoints: OVERLAY_ECG_MAX_POINTS,
  });
  const lead = data?.leads.find((l) => l.lead_name === leadName);
  return {
    timesMs: data ? data.times_ms : undefined,
    amplitudesUv: lead ? lead.amplitude_uv : undefined,
    isLoading,
    error,
  };
}

// ---------------------------------------------------------------------------
// Internal sub-components (each wraps a hook call for one study)
// ---------------------------------------------------------------------------

interface PrimaryStripProps {
  study: CompareStudy;
  leadName: string;
  mmPerSecond: number;
  mmPerMv: number;
  extraTraces: ExtraTrace[];
}

function PrimaryStrip({
  study,
  leadName,
  mmPerSecond,
  mmPerMv,
  extraTraces,
}: PrimaryStripProps) {
  const { timesMs, amplitudesUv, isLoading, error } = useStudyLead(
    study.imagingStudyId,
    leadName,
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spin size="small" />
      </div>
    );
  }

  if (error || !timesMs || !amplitudesUv) {
    return (
      <Alert
        type="warning"
        showIcon
        message={`No waveform for ${study.label}`}
        className="mb-2"
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <EcgLeadStrip
        leadName={leadName}
        timesMs={timesMs}
        amplitudesUv={amplitudesUv}
        pxPerMm={PX_PER_MM}
        mmPerSecond={mmPerSecond}
        mmPerMv={mmPerMv}
        extraTraces={extraTraces}
      />
    </div>
  );
}

/**
 * Invisible component that fires the query for a secondary study and calls
 * `onTrace` synchronously during render with the trace data (or null while
 * loading).  React Query's caching ensures this is cheap after the first load.
 */
function SecondaryTraceFetcher({
  study,
  leadName,
  onTrace,
}: {
  study: CompareStudy;
  leadName: string;
  onTrace: (trace: ExtraTrace | null) => void;
}) {
  const { timesMs, amplitudesUv } = useStudyLead(
    study.imagingStudyId,
    leadName,
  );
  onTrace(
    timesMs && amplitudesUv
      ? { timesMs, amplitudesUv, color: study.color }
      : null,
  );
  return null;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface EcgOverlayViewerProps {
  /** Studies to compare.  studies[0] is the primary (reference) strip. */
  studies: CompareStudy[];
}

/**
 * ECG overlay viewer: shows one lead strip with traces from multiple subjects
 * overlaid.  The primary study (studies[0]) is drawn in the dark default colour;
 * subsequent studies use the colour assigned to each CompareStudy.
 *
 * All viewer controls (lead, speed, gain) live inside this component.
 */
export const EcgOverlayViewer = ({ studies }: EcgOverlayViewerProps) => {
  const [leadName, setLeadName] = useState(DEFAULT_LEAD);
  const [mmPerMv, setMmPerMv] = useState(DEFAULT_GAIN_MM_PER_MV);
  const [mmPerSecond, setMmPerSecond] = useState(DEFAULT_SPEED_MM_PER_S);

  if (studies.length === 0) {
    return <Empty description="No studies selected for comparison" />;
  }

  const [primaryStudy, ...secondaryStudies] = studies;

  // Collect extra traces via synchronous callbacks — populated by the
  // SecondaryTraceFetcher renders below (same render pass).
  const extraTraces: ExtraTrace[] = [];

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Select
          size="middle"
          value={leadName}
          onChange={setLeadName}
          options={ECG_LEAD_NAMES.map((n) => ({ value: n, label: n }))}
          placeholder="Lead"
          popupMatchSelectWidth={false}
        />
        <Select
          size="middle"
          value={mmPerMv}
          onChange={setMmPerMv}
          options={GAIN_OPTIONS_MM_PER_MV.map((g) => ({
            value: g,
            label: `${g} mm/mV`,
          }))}
          popupMatchSelectWidth={false}
        />
        <Select
          size="middle"
          value={mmPerSecond}
          onChange={setMmPerSecond}
          options={SPEED_OPTIONS_MM_PER_S.map((s) => ({
            value: s,
            label: `${s} mm/s`,
          }))}
          popupMatchSelectWidth={false}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-3">
        {studies.map((study) => (
          <div
            key={study.imagingStudyId}
            className="flex items-center gap-1.5 text-xs"
          >
            <span
              className="inline-block rounded-sm"
              style={{
                width: 16,
                height: 3,
                backgroundColor: study.color,
              }}
            />
            <span className="text-gray-700">{study.label}</span>
          </div>
        ))}
      </div>

      {/* Secondary fetchers fire queries and collect trace data during render. */}
      {secondaryStudies.map((study) => (
        <SecondaryTraceFetcher
          key={study.imagingStudyId}
          study={study}
          leadName={leadName}
          onTrace={(trace) => {
            if (trace) extraTraces.push(trace);
          }}
        />
      ))}

      {/* Primary strip with all overlaid traces */}
      <PrimaryStrip
        study={primaryStudy}
        leadName={leadName}
        mmPerSecond={mmPerSecond}
        mmPerMv={mmPerMv}
        extraTraces={extraTraces}
      />
    </div>
  );
};
